import type { HostDataSource } from '@databox/ember-integrations/lib/types/host.types';
import {
	DataSourceSyncStatus as ReactSyncStatus,
	DataSourceSyncType as ReactSyncType,
	type UserActionType,
} from '@databox/types/domains/source/source.enums';

import type { BillingFeature } from 'webapp/enums/enums';
import type { VerificationInfo, VerificationInfoUser } from 'webapp/utils/verification';

import type { Source } from 'webapp/models-custom';
import { DataSourceSyncStatus, DataSourceSyncType } from 'webapp/models/source';

export const mapSourceToDataSource = (
	source: Source,
	currentUserId: number | null,
	spaceId?: number,
): HostDataSource => {
	return {
		avatar: source.realAvatar,
		connection: source.connection
			? {
					id: source.connection.id,
					name: source.connection.name,
					avatar: source.sourceType?.avatar,
					connectionNumberOfSources: source.connection.numberOfSources ?? 0,
					ownerId: source.connection.userId ?? null,
					sharableStatus: source.connection.sharableStatus ?? null,
					permissionStatus: source.connection.permissionStatus ?? null,
				}
			: undefined,
		created: source.created || null,
		createdBy: {
			userId: Number(source.createdBy?.id),
			authId: '',
			email: source.createdBy?.email ?? '',
			name: source.createdBy?.name ?? '',
			avatar: source.createdBy?.avatar ?? '',
			isOwner: source.ownerId === Number(source.createdBy?.id),
			count: 0,
		},
		id: source.numericId!,
		owner: {
			userId: source.ownerId,
			authId: '',
			email: '',
			name: '',
			avatar: '',
			count: 0,
			isOwner: source.ownerId === currentUserId,
		},
		statusObject: {
			title: source.statusTitle,
			description: source.statusDescription,
			trigger: '',
			level: source.statusObject?.level,
			id: -1,
			// The backend's action values; React handles the ones it knows and ignores the rest.
			userAction: (source.statusAction || undefined) as UserActionType | undefined,
		},
		status: source.status,
		syncInterval: source.syncInterval,
		title: source.title,
		selectedSourceOption: source.selectedSourceOption,
		type: source.type,
		typeName: source.sourceType.name,
		sourceId: +source.id!,
		initialTitle: source.initialTitle,
		subtitle: source.subtitle,
		avatarOriginal: source.realAvatar,
		wrapperFor: source.wrapperFor ?? '',
		isRoot: source.isRoot,
		isLocked: source.isLocked,
		lastActivity: null,
		metadata: null,
		metadataStatus: 0,
		permissions: {
			permissionType: source.permissionsEveryone
				? 0 // EVERYONE
				: !source.haveBasicPermissions
					? 2 // SHARED_WITH_ME
					: 1, // ME_AND_X_OTHERS
			sharedWith: source.users.filter((id: number) => id !== currentUserId).map(String),
		},
		clientId: spaceId,
		token: source.token,
		isTypeIngest: source.isTypeIngest,
		timezone: source.timezone,
		verificationInfo: mapVerificationInfo(source.verificationInfo),
		typeDetails: {
			canReconnect: !!source.connection?.canReconnect,
			isFullyEditable: source.sourceType.isWithCustomToken && !source.wrapperFor,
			canEditTimezone: source.canEditTimezone,
			canEditConnection: false,
			id: source.sourceType.id ?? 0,
			key: source.sourceType.key,
			name: source.sourceType.name,
			avatar: source.sourceType.avatar,
			wrapperFor: '',
			availableSourceOptions: source.sourceType.availableSourceOptions ?? [],
			availableMetricOptions: source.sourceType.availableMetricOptions ?? [],
		},
	};
};

// React's DataSource carries a verification's users as `userId`, the webapp's model as `id`.
const mapVerificationUser = (user: VerificationInfoUser | null) =>
	user && { userId: user.id, name: user.name, avatar: user.avatar ?? null };

const mapVerificationInfo = (info?: VerificationInfo): HostDataSource['verificationInfo'] =>
	info && {
		status: info.status,
		created: info.created,
		createdBy: mapVerificationUser(info.createdBy),
		modified: info.modified,
		modifiedBy: mapVerificationUser(info.modifiedBy),
	};

export const mapSyncInfo = (lastSync: Source['lastSync']) => {
	if (!lastSync) {
		return undefined;
	}

	return {
		startedAt: lastSync.startedAt,
		finishedAt: lastSync?.finishedAt,
		syncType: mapSyncType(lastSync.syncType),
		syncStatus: mapSyncStatus(lastSync.syncStatus),
		lastSuccessfulSyncAt: lastSync?.lastSuccessfulSyncAt,
		dataRangeFrom: lastSync?.dataRangeFrom,
		dataRangeTo: lastSync?.dataRangeTo,
		dataAvailableAt: lastSync?.dataAvailableAt,
	};
};

const mapSyncType = (syncType: DataSourceSyncType): ReactSyncType => {
	switch (syncType) {
		case DataSourceSyncType.INITIAL_SYNC:
			return ReactSyncType.INITIAL_SYNC;
		case DataSourceSyncType.USER_REQUEST:
			return ReactSyncType.USER_REQUEST;
		case DataSourceSyncType.MODIFICATION_REQUEST:
			return ReactSyncType.MODIFICATION_REQUEST;
		case DataSourceSyncType.SCHEDULED_UPDATE:
			return ReactSyncType.SCHEDULED_UPDATE;
	}
};

const mapSyncStatus = (syncStatus: DataSourceSyncStatus): ReactSyncStatus => {
	switch (syncStatus) {
		case DataSourceSyncStatus.SUCCESS:
			return ReactSyncStatus.SUCCESS;
		case DataSourceSyncStatus.FAILED:
			return ReactSyncStatus.FAILED;
		case DataSourceSyncStatus.IN_PROGRESS:
			return ReactSyncStatus.IN_PROGRESS;
		case DataSourceSyncStatus.PARTIAL_SYNC:
			return ReactSyncStatus.PARTIALLY_DONE;
		case DataSourceSyncStatus.SUCCESS_WITH_WARNINGS:
			return ReactSyncStatus.SUCCESS_WITH_WARNINGS;
		case DataSourceSyncStatus.CANCELED:
			return ReactSyncStatus.CANCELED;
	}
};

export const getBillingFeature = (featureName: string): BillingFeature =>
	featureName as BillingFeature;
